/**
 * Capsule Manager — CRUD modal for shareable conversation links
 */

import { state } from './state.js';
import { escapeHtml } from './html-escape.js';
import { createCapsule, listCapsules, updateCapsule, deleteCapsule } from './api.js';

let currentSessionId = null;
let editingCapsuleId = null;

const els = {};

export function initCapsuleManager() {
    els.modal = document.getElementById('capsule-modal');
    els.closeBtn = document.getElementById('capsule-modal-close');
    els.listView = document.getElementById('capsule-list-view');
    els.list = document.getElementById('capsule-list');
    els.createBtn = document.getElementById('capsule-create-btn');
    els.formView = document.getElementById('capsule-form-view');
    els.labelInput = document.getElementById('capsule-label');
    els.expiresInput = document.getElementById('capsule-expires');
    els.allowPrompts = document.getElementById('capsule-allow-prompts');
    els.allowVoice = document.getElementById('capsule-allow-voice');
    els.noExpiry = document.getElementById('capsule-no-expiry');
    els.cancelBtn = document.getElementById('capsule-form-cancel');
    els.saveBtn = document.getElementById('capsule-form-save');

    if (!els.modal) return;

    // Close modal
    els.closeBtn.addEventListener('click', closeModal);
    els.modal.addEventListener('click', (e) => {
        if (e.target === els.modal) closeModal();
    });

    // Create button
    els.createBtn.addEventListener('click', () => showForm());

    // Cancel form
    els.cancelBtn.addEventListener('click', () => showList());

    // No-expiry toggle disables/enables the datetime input
    els.noExpiry.addEventListener('change', () => {
        els.expiresInput.disabled = els.noExpiry.checked;
        if (els.noExpiry.checked) {
            els.expiresInput.value = '';
        }
    });

    // Save
    els.saveBtn.addEventListener('click', handleSave);
}

export async function openCapsuleManager(sessionId) {
    if (!sessionId || !els.modal) return;
    currentSessionId = sessionId;
    editingCapsuleId = null;
    els.modal.classList.remove('hidden');
    els.modal.classList.add('active');
    showList();
    await loadCapsules();
}

function closeModal() {
    els.modal.classList.remove('active');
    els.modal.classList.add('hidden');
    currentSessionId = null;
    editingCapsuleId = null;
}

function showList() {
    els.listView.classList.remove('hidden');
    els.formView.classList.add('hidden');
    editingCapsuleId = null;
}

function showForm(capsule = null) {
    els.listView.classList.add('hidden');
    els.formView.classList.remove('hidden');

    if (capsule) {
        // Edit mode
        editingCapsuleId = capsule.id;
        els.labelInput.value = capsule.label || '';
        const hasExpiry = !!capsule.expires_at;
        els.noExpiry.checked = !hasExpiry;
        els.expiresInput.disabled = !hasExpiry;
        els.expiresInput.value = hasExpiry ? capsule.expires_at.slice(0, 16) : '';
        els.allowPrompts.checked = capsule.allow_prompts;
        els.allowVoice.checked = capsule.allow_voice;
        els.saveBtn.textContent = 'Save Changes';
    } else {
        // Create mode — default 7 days from now
        editingCapsuleId = null;
        els.labelInput.value = '';
        els.noExpiry.checked = false;
        els.expiresInput.disabled = false;
        const defaultExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
        els.expiresInput.value = defaultExpiry.toISOString().slice(0, 16);
        els.allowPrompts.checked = false;
        els.allowVoice.checked = false;
        els.saveBtn.textContent = 'Create Link';
    }
}

async function handleSave() {
    const label = els.labelInput.value.trim();
    const expiresVal = els.expiresInput.value;
    const allowPrompts = els.allowPrompts.checked;
    const allowVoice = els.allowVoice.checked;

    // Build expires_at ISO string
    let expires_at = null;
    if (expiresVal) {
        expires_at = new Date(expiresVal).toISOString();
    }

    els.saveBtn.disabled = true;
    els.saveBtn.textContent = editingCapsuleId ? 'Saving...' : 'Creating...';

    try {
        if (editingCapsuleId) {
            await updateCapsule(editingCapsuleId, {
                label: label || null,
                expires_at,
                allow_prompts: allowPrompts,
                allow_voice: allowVoice,
            });
        } else {
            await createCapsule(currentSessionId, {
                label: label || null,
                expires_at,
                allow_prompts: allowPrompts,
                allow_voice: allowVoice,
            });
        }

        showList();
        await loadCapsules();
    } catch (e) {
        console.error('[CapsuleManager] Save error:', e);
    } finally {
        els.saveBtn.disabled = false;
        els.saveBtn.textContent = editingCapsuleId ? 'Save Changes' : 'Create Link';
    }
}

async function loadCapsules() {
    if (!currentSessionId) return;

    els.list.innerHTML = '<div class="capsule-loading">Loading...</div>';

    try {
        const data = await listCapsules(currentSessionId);
        const capsules = data.capsules || [];

        if (capsules.length === 0) {
            els.list.innerHTML = '<div class="capsule-empty">No shared links yet. Create one to share this conversation.</div>';
            return;
        }

        els.list.innerHTML = '';
        for (const cap of capsules) {
            els.list.appendChild(renderCapsuleItem(cap));
        }
    } catch (e) {
        els.list.innerHTML = '<div class="capsule-empty">Failed to load links.</div>';
        console.error('[CapsuleManager] Load error:', e);
    }
}

function renderCapsuleItem(cap) {
    const item = document.createElement('div');
    item.className = 'capsule-list-item';

    const isExpired = cap.expires_at && new Date(cap.expires_at) < new Date();
    const isInactive = !cap.is_active;
    const statusClass = isExpired ? 'expired' : isInactive ? 'inactive' : 'active';

    const label = cap.label || 'Untitled';
    const mode = cap.allow_prompts ? 'Interactive' : 'Read-only';
    const modeClass = cap.allow_prompts ? 'interactive' : 'readonly';

    let expiryText = 'No expiration';
    if (cap.expires_at) {
        const d = new Date(cap.expires_at);
        expiryText = isExpired ? `Expired ${formatRelative(d)}` : `Expires ${formatRelative(d)}`;
    }

    item.innerHTML = `
        <div class="capsule-item-header">
            <span class="capsule-item-label">${escapeHtml(label)}</span>
            <div class="capsule-item-badges">
                <span class="capsule-badge ${modeClass}">${mode}</span>
                <span class="capsule-badge ${statusClass}">${isExpired ? 'Expired' : isInactive ? 'Disabled' : 'Active'}</span>
            </div>
        </div>
        <div class="capsule-item-meta">${expiryText}</div>
        <div class="capsule-item-actions">
            <button class="capsule-action-btn capsule-copy-btn" title="Copy link">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <span>Copy</span>
            </button>
            <button class="capsule-action-btn capsule-edit-btn" title="Edit">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
            </button>
            <button class="capsule-action-btn capsule-toggle-btn" title="${cap.is_active ? 'Disable' : 'Enable'}">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    ${cap.is_active
                        ? '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>'
                        : '<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/>'
                    }
                </svg>
            </button>
            <button class="capsule-action-btn capsule-delete-btn" title="Delete">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
            </button>
        </div>
    `;

    // Copy link
    item.querySelector('.capsule-copy-btn').addEventListener('click', () => {
        const url = cap.url || `${window.location.origin}?c=${cap.token}`;
        navigator.clipboard.writeText(url).then(() => {
            const btn = item.querySelector('.capsule-copy-btn span');
            btn.textContent = 'Copied!';
            setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
        });
    });

    // Edit
    item.querySelector('.capsule-edit-btn').addEventListener('click', () => showForm(cap));

    // Toggle active
    item.querySelector('.capsule-toggle-btn').addEventListener('click', async () => {
        await updateCapsule(cap.id, { is_active: !cap.is_active });
        await loadCapsules();
    });

    // Delete
    item.querySelector('.capsule-delete-btn').addEventListener('click', async () => {
        if (confirm('Delete this shared link? The copied conversation will also be removed.')) {
            await deleteCapsule(cap.id);
            await loadCapsules();
        }
    });

    return item;
}

function formatRelative(date) {
    const now = new Date();
    const diff = date - now;
    const absDiff = Math.abs(diff);
    const past = diff < 0;

    if (absDiff < 60000) return past ? 'just now' : 'in a moment';
    if (absDiff < 3600000) {
        const mins = Math.round(absDiff / 60000);
        return past ? `${mins}m ago` : `in ${mins}m`;
    }
    if (absDiff < 86400000) {
        const hrs = Math.round(absDiff / 3600000);
        return past ? `${hrs}h ago` : `in ${hrs}h`;
    }
    const days = Math.round(absDiff / 86400000);
    return past ? `${days}d ago` : `in ${days}d`;
}


